import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import GenericTabNavigation from '../components/GenericTabNavigation';
import { RAGIndexer, RAGIndexerStyles } from '../components/RAGIndexer';
import RagArticlesIndex from '../components/RagArticlesIndex';
import RagIndexStatus from '../components/RagIndexStatus';
import ErrorModal from '../components/ErrorModal';
import { checkIsMaster } from '../utils/config-calls';
import { routedFetch } from '../utils/api-router';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

/**
 * Page de gestion RAG
 * Regroupe l'indexation RAG et la visualisation des articles indexés
 * Nécessite d'être authentifié avec une clé MASTER
 */
export default function RAGManagementPage() {
  const { t, loadPageTranslations } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('indexing');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [wa_world, setWaWorld] = useState(null);
  const [ragStats, setRagStats] = useState(null);

  // Récupérer wa_world depuis localStorage
  useEffect(() => {
    try {
      const storedWorld = localStorage.getItem('wa_world');
      if (storedWorld) {
        const world = JSON.parse(storedWorld);
        setWaWorld(world);
        debug.log(config, 'wa_world récupéré depuis localStorage:', world);
      } else {
        debug.warn(config, 'wa_world non trouvé dans localStorage');
      }
    } catch (err) {
      debug.error(config, 'Erreur lors de la récupération de wa_world:', err);
    }
  }, []);

  // Debug
  useEffect(() => {
    debug.log(config, 'RAGManagementPage - wa_world:', wa_world);
  }, [wa_world]);

  // Charger le statut RAG
  useEffect(() => {
    const fetchRagStats = async () => {
      if (!wa_world) return;
      
      try {
        const response = await routedFetch(`/api/rag/status?worldId=${wa_world.id}`);
        if (response.data) {
          debug.log(config, '[RAG Status] Stats loaded:', response.data);
          setRagStats(response.data);
        }
      } catch (err) {
        debug.warn(config, 'Could not fetch RAG stats:', err);
      }
    };

    // Récupérer le statut immédiatement et puis toutes les 10 secondes
    fetchRagStats();
    const interval = setInterval(fetchRagStats, 10000);

    return () => clearInterval(interval);
  }, [wa_world]);

  // Charger les traductions pour la page
  useEffect(() => {
    loadPageTranslations('ADMIN');
    loadPageTranslations('CHATBOT');
  }, [loadPageTranslations]);

  // Vérifier si l'utilisateur est authentifié et admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      try {
        setChecking(true);
        const result = await checkIsMaster();

        if (result.success && result.isMaster) {
          setIsAdmin(true);
          debug.log(config, 'Accès administrateur confirmé pour RAG');
        } else {
          setIsAdmin(false);
          setError({
            title: t('ADMIN', 'ACCESS_DENIED', 'Accès refusé'),
            message: t('ADMIN', 'NEED_MASTER_KEY', 'Vous devez disposer de la clé MASTER pour accéder à cette section.'),
          });
          debug.warn(config, 'Accès administrateur refusé pour RAG');
        }
      } catch (err) {
        setIsAdmin(false);
        setError({
          title: t('ADMIN', 'ACCESS_ERROR', 'Erreur de vérification'),
          message: t('ADMIN', 'CHECK_FAILED', 'Impossible de vérifier vos droits d\'accès.'),
        });
        debug.error(config, 'Erreur lors de la vérification des droits admin pour RAG', err);
      } finally {
        setChecking(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, navigate, t]);

  // Onglets disponibles
  const tabs = [
    { id: 'indexing', label: t('ADMIN', 'TAB_RAG_INDEXING', 'Indexation RAG') },
    { id: 'articles', label: t('ADMIN', 'RAG_ARTICLES_INDEX', 'Articles indexés') },
  ];

  // Fermer la modal d'erreur et rediriger vers la page d'accueil
  const handleErrorClose = () => {
    setError(null);
    navigate('/');
  };

  // Afficher l'interface de chargement si nécessaire
  if (checking) {
    return (
      <div className="wa-admin-page loading">
        <h1>{t('ADMIN', 'CHECKING_ACCESS', 'Vérification des droits d\'accès...')}</h1>
        <div className="wa-loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return <ErrorModal error={error} onClose={handleErrorClose} />;
  }

  return isAdmin ? (
    <div className="wa-rag-management-page">
      <h1>{t('ADMIN', 'RAG_MANAGEMENT', 'Gestion RAG')}</h1>

      {ragStats && (
        <div className="wa-rag-status-banner" style={{ marginBottom: '20px', padding: '12px', backgroundColor: 'var(--color-primary-light)', borderRadius: 'var(--radius-lg)', borderLeft: '4px solid var(--color-primary)' }}>
          <RagIndexStatus status={ragStats} world={wa_world} />
        </div>
      )}

      {wa_world ? (
        <>
           <GenericTabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          >
            <div className="wa-rag-content">
              {activeTab === 'indexing' && (
                <>
                  <style>{RAGIndexerStyles}</style>
                  <RAGIndexer worldId={wa_world.id} />
                </>
              )}
              {activeTab === 'articles' && <RagArticlesIndex world={wa_world} />}
            </div>
          </GenericTabNavigation>
        </>
      ) : (
        <div className="wa-no-world-selected">
          <h2>{t('ADMIN', 'NO_WORLD', 'Aucun monde sélectionné')}</h2>
          <p>{t('ADMIN', 'SELECT_WORLD_RAG', 'Veuillez sélectionner un monde WorldAnvil pour gérer l\'indexation RAG.')}</p>
        </div>
      )}
    </div>
  ) : (
    <div className="wa-admin-access-denied">
      <h2>{t('ADMIN', 'ACCESS_DENIED', 'Accès refusé')}</h2>
      <p>{t('ADMIN', 'NEED_MASTER_KEY', 'Vous devez disposer de la clé MASTER pour accéder à cette section.')}</p>
    </div>
  );
}
